import { makeStyles } from '@material-ui/styles'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { without } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  addHostToAggregate,
  getResources,
  listHypervisors,
  listResmgrHosts,
  listResourceInventory,
  removeHostFromAggregate,
  updateHostAggregate,
  updateHostAggregateMetadata,
  updateResourceInventory,
} from '../actions'
import ClusterHostGroupsPicklist from '../clusters/ClusterHostGroupsPicklist'
import { mergeHypervisorsWithHosts } from '../helpers'
import { hypervisorsSelector, resmgrHostsSelector } from '../selectors'
import { hypervisorColumns } from './AddHostAggregateModal'

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    height: 'fit-content',
  },
}))

interface Props {
  rows: any[]
  onClose: () => void
}

export default function EditHostAggregateModal({ rows: [hostAggregate], onClose }: Props) {
  const classes = useStyles()
  const initialKeys = useMemo(() => {
    return Object.keys(hostAggregate.metadata).filter((key) => key !== 'availability_zone')
  }, [hostAggregate.metadata])

  const defaultParams = useMemo(
    () => ({
      name: hostAggregate.name,
      availabilityZone: hostAggregate.availability_zone ? hostAggregate.availability_zone : '',
      hosts: [],
      metadata: Object.entries(hostAggregate.metadata)
        .map(([key, value]) => {
          return { key, value }
        })
        .filter(({ key }) => key !== 'availability_zone'),
    }),
    [hostAggregate],
  )

  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)
  const [submitting, setSubmitting] = useState(false)
  const [resources, setResources] = useState([])

  const { update, updating, error, reset } = useUpdateAction(updateHostAggregate)
  const {
    update: addHost,
    updating: addingHost,
    error: errorAddingHost,
    reset: resetAddHost,
  } = useUpdateAction(addHostToAggregate)
  const {
    update: removeHost,
    updating: removingHost,
    error: errorRemovingHost,
    reset: resetRemoveHost,
  } = useUpdateAction(removeHostFromAggregate)
  const {
    update: updateMetadata,
    updating: updatingMetadata,
    error: errorUpdatingMetadata,
    reset: resetUpdateMetadata,
  } = useUpdateAction(updateHostAggregateMetadata)

  const { loading: loadingHosts } = useListAction(listResmgrHosts)
  const hosts = useSelector(resmgrHostsSelector)
  const { loading: loadingHypervisors } = useListAction(listHypervisors, {
    params: {},
  })
  const hypervisorsList = useSelector(hypervisorsSelector)

  // Filter hosts by selected cluster
  const filteredHostByCluster = useMemo(() => {
    return hosts.filter((host) => host.clusterName === params.availabilityZone)
  }, [hosts, params.availabilityZone])

  const validHypervisors = useMemo(() => {
    const list = hypervisorsList?.length ? hypervisorsList : hostAggregate?.hosts || []
    return mergeHypervisorsWithHosts(list, filteredHostByCluster)
  }, [filteredHostByCluster, hypervisorsList])

  const selectedHosts = useMemo(() => {
    const hostnames = params.hosts.flatMap((host) => host.hypervisor_hostname)
    return validHypervisors.filter((hypervisor) =>
      hostnames.includes(hypervisor?.hypervisor_hostname),
    )
  }, [validHypervisors, params.hosts])

  useEffect(() => {
    const getAllRsources = async () => {
      const resources = await getResources()
      setResources(resources)
    }

    getAllRsources()
  }, [])

  useEffect(() => {
    const selectedHypervisors = hypervisorsList.filter((hypervisor) => {
      return hostAggregate.hosts?.includes(hypervisor?.service?.host)
    })
    updateParams({ hosts: selectedHypervisors })
  }, [hostAggregate.hosts, hypervisorsList])

  const submitForm = useCallback(async () => {
    setSubmitting(true)
    const body = {
      aggregate: {
        name: params.name,
        availability_zone: params.availabilityZone,
      },
    }
    const metadata = params.metadata.reduce((accum, pair) => {
      // Don't let user set availability zone
      if (pair.key === 'availability_zone') {
        return accum
      }
      return {
        ...accum,
        [pair.key]: pair.value,
      }
    }, {})
    const currentKeys = Object.keys(metadata)
    const removedKeys = without(currentKeys, initialKeys)
    const removeObject = removedKeys.reduce((accum, key) => {
      return {
        ...accum,
        [key]: null,
      }
    }, {})
    const metadataBody = {
      set_metadata: {
        metadata: {
          ...metadata,
          ...removeObject,
        },
      },
    }
    const hostIdsToAdd = params.hosts
      .filter((host) => {
        return !hostAggregate.hosts.includes(host.service.host)
      })
      .map((host) => host.service.host)
    const hostIdsToRemove = hostAggregate.hosts.filter((host) => {
      const desiredHostIds = params.hosts.map((host) => host.service.host)
      return !desiredHostIds.includes(host)
    })

    const { success } = await update({ id: hostAggregate.id, body })
    if (!success) {
      setSubmitting(false)
      return
    }
    updateMetadata({ id: hostAggregate.id, body: metadataBody })
    const addPromises = hostIdsToAdd.map((host) => {
      return addHost({
        id: hostAggregate.id,
        body: {
          add_host: {
            host: host,
          },
        },
      })
    })
    const removePromises = hostIdsToRemove.map((host) => {
      return removeHost({
        id: hostAggregate.id,
        body: {
          remove_host: {
            host: host,
          },
        },
      })
    })

    const filterResourcesUUIDByHostName = resources
      .filter((resource) => {
        const filteredHostByName = params.hosts.map((host) => host?.hypervisor_hostname)
        return filteredHostByName.includes(resource.name)
      })
      .map((resource) => resource.uuid)

    const allocationRatioPromises = filterResourcesUUIDByHostName.map(async (uuid) => {
      const { resource_provider_generation, inventories } = await listResourceInventory({
        id: uuid,
      })

      const filteredInvetories = Object.keys(inventories).reduce((accum, current) => {
        return {
          ...accum,
          [current]: {
            ...inventories[current],
            allocation_ratio: !!params[current]
              ? Number(params[current])
              : inventories[current].allocation_ratio,
          },
        }
      }, {})

      return updateResourceInventory({
        id: uuid,
        body: {
          inventories: filteredInvetories,
          resource_provider_generation: resource_provider_generation,
        },
      })
    })

    const promiseData = await Promise.allSettled([
      ...addPromises,
      ...removePromises,
      ...allocationRatioPromises,
    ])

    if (promiseData) handleClose()

    setSubmitting(false)
  }, [hostAggregate, params])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    resetAddHost()
    resetRemoveHost()
    resetUpdateMetadata()
    // reloadAvailabilityZones(true, true)
    onClose()
  }

  const isPf9ManagedHostAggregate = useMemo(() => {
    return hostAggregate?.metadata?.['pf9-managed']
  }, [hostAggregate])

  return (
    <ModalForm
      title={`Edit Host Aggregate`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={submitting}
      error={error}
      submitTitle={`Update Host Aggregate`}
      open
    >
      <>
        <FormFieldSection title="Host Aggregate Settings" alignContentLeft>
          <div className={classes.fields}>
            <TextField
              id="name"
              label="Name"
              onChange={getParamsUpdater('name')}
              value={params.name}
              readOnly={isPf9ManagedHostAggregate}
              disabled={isPf9ManagedHostAggregate}
              required
            />
            <PicklistField
              id="availabilityZone"
              label="Cluster"
              info="Choose the cluster that this host aggregate should be placed on"
              onChange={getParamsUpdater('availabilityZone')}
              value={params.availabilityZone}
              disableClusterWithoutHosts
              fullWidth
              required
              DropdownComponent={ClusterHostGroupsPicklist}
            />
          </div>
          <KeyValuesField
            id="metadata"
            label="Metadata"
            value={params.metadata}
            onChange={getParamsUpdater('metadata')}
            addLabel="Add Metadata"
            allowMultipleValues
          />
        </FormFieldSection>
        {!isPf9ManagedHostAggregate && (
          <FormFieldSection title="Select Hosts to Add" alignContentLeft>
            <ListTableField
              id="hosts"
              data={validHypervisors}
              loading={loadingHypervisors || loadingHosts}
              columns={hypervisorColumns}
              onChange={getParamsUpdater('hosts')}
              value={selectedHosts}
              uniqueIdentifier="id"
              searchTargets={['hypervisor_hostname']}
              emptyText={
                params.availabilityZone
                  ? 'No matching hosts found for selected cluster'
                  : 'Please select a cluster first'
              }
              multiSelection
            />
          </FormFieldSection>
        )}
      </>
    </ModalForm>
  )
}
